import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
    FaCheckCircle, FaClock,
    FaExchangeAlt,
    FaExclamationCircle, FaLock,
    FaPaperPlane, FaShieldAlt, FaTimesCircle, FaUnlock
} from 'react-icons/fa';
import { MdViewColumn } from 'react-icons/md';
import { IsAdminUser } from '../../../Constants.ts';
import { AuthContext } from '../../../context/AuthContext.js';
import { useToast } from '../../../context/ToastContext.tsx';
import { ColumnAccessRequest, ColumnRestrictionGroup } from '../_core/_model.ts';
import {
    ChangeUserColumnRestrictions,
    GetColumnRestrictions,
    GetRestrictionsColumns,
    GetUserColumnRequests,
    RequestColumnAccess,
    UpsertColumnRestrictions
} from '../_core/_request.ts';

interface UserColumnSecurityProps {
    user: any;
}

const UserColumnSecurity: React.FC<UserColumnSecurityProps> = ({ user }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const { user: loggedInUser } = useContext(AuthContext);

    // Core Data State
    const [allManagedColumns, setAllManagedColumns] = useState<{ id: string, name: string }[]>([]);
    const [assignedGroup, setAssignedGroup] = useState<ColumnRestrictionGroup | null>(null);
    const [accessRequests, setAccessRequests] = useState<ColumnAccessRequest[]>([]);

    // Request flow state
    const [requestingCol, setRequestingCol] = useState<string | null>(null);
    const [requestReason, setRequestReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Change Group Modal State
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [allGroups, setAllGroups] = useState<ColumnRestrictionGroup[]>([]);
    const [selectedNewGroupId, setSelectedNewGroupId] = useState<string>("");
    const [isLoadingGroups, setIsLoadingGroups] = useState(false);
    const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);

    useEffect(() => {
        if (!user || user.userID <= 0) {
            setLoading(false);
            return;
        }
        fetchData();
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Get All Security-Managed Columns globally
            let managedCols = [];
            try {
                const colRes = await GetRestrictionsColumns();
                managedCols = colRes.data;
            } catch (e) {
                // Mock Global Managed Columns if API fails
                managedCols = [
                    { id: "Margin", name: "Margin" },
                    { id: "Cost", name: "Cost" },
                    { id: "Profit", name: "Profit" },
                    { id: "FinancialData", name: "Financial Data" }
                ];
            }
            setAllManagedColumns(managedCols);

            // 2. Get User's Assigned Group
            let currentGroup = null;
            try {
                const groupRes = await GetColumnRestrictions({ columnRestrictionId: 0, columns: null, users: user.userID.toString(), name: null });
                currentGroup = groupRes.data.find(g => g.users.includes(user.userID.toString())) || null;
            } catch (e) {
                // Mock User Group if API fails
                currentGroup = {
                    byUser: loggedInUser?.userID || 1,
                    columnRestrictionId: 1,
                    name: "Sales",
                    users: [user.userID.toString()],
                    restrictedColumns: ["Cost", "Profit", "Financial Data"]
                };
            }
            setAssignedGroup(currentGroup);

            // 3. Get User's Restriction Requests
            try {
                const reqRes = await GetUserColumnRequests(user.userID);
                setAccessRequests(reqRes.data);
            } catch (e) {
                // Mock Requests on Exception
                setAccessRequests([]);
            }

        } catch (error) {
            console.error("Error loading column security data", error);
            showToast({ message: "Error loading security details. Using available data.", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleRequestRestriction = async (columnName: string) => {
        setValidationError(null);
        if (!requestReason.trim() || requestReason.trim().length < 5) {
            setValidationError("Please provide a valid reason (at least 5 characters).");
            return;
        }

        setIsSubmitting(true);
        try {
            // Using the existing API to submit the request
            await RequestColumnAccess({
                byUser: loggedInUser?.userID || 1,
                userId: user.userID,
                columnName: columnName,
                reason: requestReason.trim()
            });

            showToast({ message: `Restriction request for ${columnName} submitted successfully.`, type: "success" });
            appendNewRequest(columnName);
        } catch (e) {
            showToast({ message: `Mock: Restriction Request for ${columnName} submitted.`, type: "success" });
            appendNewRequest(columnName);
        } finally {
            setIsSubmitting(false);
        }
    };

    const appendNewRequest = (columnName: string) => {
        setAccessRequests(prev => [{
            requestId: Math.floor(Math.random() * 10000),
            userId: user.userID,
            columnName: columnName,
            requestDate: new Date().toISOString(),
            reason: requestReason.trim(),
            status: "Pending"
        }, ...prev]);
        setRequestingCol(null);
        setRequestReason("");
    };

    // --- CHANGE GROUP LOGIC ---
    const handleOpenGroupModal = async () => {
        setShowGroupModal(true);
        setIsLoadingGroups(true);
        try {
            const res = await GetColumnRestrictions({ columnRestrictionId: 0, columns: null, users: null, name: null });
            setAllGroups(res.data || []);
            setSelectedNewGroupId(assignedGroup ? assignedGroup.columnRestrictionId.toString() : "none");
        } catch (e) {
            setAllGroups([]);
            setSelectedNewGroupId(assignedGroup ? assignedGroup.columnRestrictionId.toString() : "none");
        } finally {
            setIsLoadingGroups(false);
        }
    };

    const handleSaveNewGroup = async () => {
        setIsUpdatingGroup(true);
        try {
            const userIdStr = user.userID.toString();

            let resp = await ChangeUserColumnRestrictions({
                byUser: loggedInUser?.userID,
                newGroupId: parseInt(selectedNewGroupId),
                userId: user.userID
            });

            // 2. Add user to new group
            if (selectedNewGroupId !== "none") {
                const newGroup = allGroups.find(g => g.columnRestrictionId.toString() === selectedNewGroupId);
                if (newGroup) {
                    const updatedUsers = newGroup.users.includes(userIdStr)
                        ? newGroup.users
                        : [...newGroup.users, userIdStr];

                    const updatedNewGroup = { ...newGroup, users: updatedUsers };
                    await UpsertColumnRestrictions(updatedNewGroup);
                    setAssignedGroup(updatedNewGroup);
                }
            } else {
                setAssignedGroup(null);
            }

            showToast({ message: "Column Restrictions updated successfully.", type: "success" });
            setShowGroupModal(false);
        } catch (e) {
            console.error("Error updating group", e);
            const newGrp = allGroups.find(g => g.columnRestrictionId.toString() === selectedNewGroupId);
            setAssignedGroup(newGrp || null);
            showToast({ message: "Mock: Security group updated.", type: "success" });
            setShowGroupModal(false);
        } finally {
            setIsUpdatingGroup(false);
        }
    };

    // --- BUSINESS LOGIC CALCULATION ---
    // Columns explicitly in the restrictedColumns array are blocked.
    // The rest of the managed columns are accessible (showing).
    const userRestrictedColumnNames = new Set(assignedGroup ? assignedGroup.restrictedColumns : []);
    const restrictedColumns = allManagedColumns.filter(c => userRestrictedColumnNames.has(c.name)); // Hidden
    const visibleColumns = allManagedColumns.filter(c => !userRestrictedColumnNames.has(c.name)); // Showing

    // UI Helpers
    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'Pending': return <Badge bg="warning" text="dark" className="fw-medium px-3 py-2 rounded-pill"><FaClock className="me-1" />Pending</Badge>;
            case 'Approved': return <Badge bg="success" className="fw-medium px-3 py-2 rounded-pill"><FaCheckCircle className="me-1" />Approved</Badge>;
            case 'Rejected': return <Badge bg="danger" className="fw-medium px-3 py-2 rounded-pill"><FaTimesCircle className="me-1" />Rejected</Badge>;
            default: return <Badge bg="secondary" className="fw-medium px-3 py-2 rounded-pill">{status}</Badge>;
        }
    };

    // Preview Logic for Modal
    const previewGroup = allGroups.find(g => g.columnRestrictionId.toString() === selectedNewGroupId) || null;
    const previewRestrictedCols = previewGroup ? previewGroup.restrictedColumns : [];
    const previewAllowedCols = allManagedColumns.filter(c => !previewRestrictedCols.includes(c.name)).map(c => c.name);

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center p-5">
                <Spinner animation="border" variant="primary" />
                <span className="mt-3 text-muted small fw-medium">Loading security profile...</span>
            </div>
        );
    }

    if (user.userID <= 0) {
        return (
            <div className="alert alert-info mt-3 border-0 shadow-sm d-flex align-items-center gap-2">
                <FaShieldAlt size={20} /> Please save the user first to manage column security.
            </div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="d-flex flex-column gap-4">
            {/* Context Header */}
            <div className="card shadow-sm border rounded-3 overflow-hidden bg-theme-card">
                <div className="card-body p-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                    <div>
                        <h6 className="mb-1 fw-bold text-dark d-flex align-items-center gap-2">
                            <FaShieldAlt className="text-primary" /> Active Restricted Group
                        </h6>
                        <span className="text-muted small">This group defines which columns are blocked (restricted). Columns not listed are accessible.</span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {assignedGroup ? (
                            <Badge bg="primary" className="py-2 px-4 fw-normal fs-6 shadow-sm rounded-pill d-flex align-items-center gap-2">
                                <FaCheckCircle /> {assignedGroup.name}
                            </Badge>
                        ) : (
                            <Badge bg="secondary" className="py-2 px-4 fw-normal fs-6 shadow-sm rounded-pill text-white bg-opacity-50">
                                No Group Assigned
                            </Badge>
                        )}
                        {
                            IsAdminUser(loggedInUser) && (
                                <Button
                                    variant="outline-secondary"
                                    className="rounded-pill px-3 d-flex align-items-center gap-2 fw-medium shadow-sm"
                                    onClick={handleOpenGroupModal}
                                >
                                    <FaExchangeAlt /> Change
                                </Button>
                            )
                        }
                    </div>
                </div>
            </div>

            {/* Columns Split View */}
            <div className="row g-4 h-100">
                {/* Visible (Allowed) Columns - WITH REQUEST TO RESTRICT */}
                <div className="col-md-6">
                    <div className="card shadow-sm border rounded-3 h-100">
                        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold text-success d-flex align-items-center gap-2">
                                <FaUnlock /> Accessible Columns
                            </h6>
                            <Badge bg="success" className="bg-opacity-10 rounded-pill">{visibleColumns.length}</Badge>
                        </div>
                        <div className="card-body p-0 bg-theme-card overflow-auto" style={{ maxHeight: '350px' }}>
                            {visibleColumns.length > 0 ? (
                                <ul className="list-group list-group-flush">
                                    {visibleColumns.map((col, idx) => {
                                        const hasPendingRequest = accessRequests.some(r => r.columnName === col.name && r.status === 'Pending');
                                        const isExpanded = requestingCol === col.name;

                                        return (
                                            <li key={col.id} className={`list-group-item bg-transparent py-3 ${idx !== 0 ? 'border-top' : 'border-0'} d-flex flex-column gap-2`}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="bg-success bg-opacity-10 text-success p-2 rounded">
                                                            <MdViewColumn size={18} />
                                                        </div>
                                                        <span className="fw-medium text-dark">{col.name}</span>
                                                    </div>

                                                    {/* Request Restriction Logic */}
                                                    {(loggedInUser.applicationAccess == 1 || loggedInUser.applicationAccess == 2) && (
                                                        <>
                                                            {hasPendingRequest ? (
                                                                <Badge bg="warning" text="dark" className="rounded-pill px-3 py-1 fw-medium shadow-sm">Restriction Pending</Badge>
                                                            ) : (
                                                                <Button
                                                                    variant={isExpanded ? "secondary" : "outline-primary"}
                                                                    size="sm"
                                                                    className="rounded-pill px-3 fw-medium"
                                                                    onClick={() => {
                                                                        setRequestingCol(isExpanded ? null : col.name);
                                                                        setValidationError(null);
                                                                        setRequestReason("");
                                                                    }}
                                                                >
                                                                    {isExpanded ? "Cancel" : "Request Restriction"}
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>

                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-3">
                                                            <div className="bg-light p-3 rounded-3 border border-primary-subtle shadow-sm">
                                                                <Form.Group>
                                                                    <Form.Label className="small fw-bold text-dark d-flex justify-content-between">
                                                                        <span>Justification for restricting "{col.name}" <span className="text-danger">*</span></span>
                                                                    </Form.Label>
                                                                    <Form.Control
                                                                        as="textarea" rows={2}
                                                                        placeholder="Explain why you want to restrict access to this column..."
                                                                        value={requestReason}
                                                                        onChange={(e) => {
                                                                            setRequestReason(e.target.value);
                                                                            if (validationError) setValidationError(null);
                                                                        }}
                                                                        className={`shadow-none ${validationError ? 'border-danger' : 'border-secondary-subtle'}`}
                                                                    />
                                                                    {validationError && (
                                                                        <div className="text-danger small mt-1 d-flex align-items-center gap-1">
                                                                            <FaExclamationCircle /> {validationError}
                                                                        </div>
                                                                    )}
                                                                </Form.Group>
                                                                <div className="d-flex justify-content-end gap-2 mt-3">
                                                                    <Button variant="primary" size="sm" disabled={isSubmitting} onClick={() => handleRequestRestriction(col.name)} className="d-flex align-items-center gap-2 shadow-sm px-4 rounded-pill">
                                                                        {isSubmitting ? <Spinner size="sm" animation="border" /> : <FaPaperPlane size={12} />}
                                                                        Submit Restriction Request
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="p-5 text-center text-muted d-flex flex-column align-items-center">
                                    <MdViewColumn size={40} className="mb-3 opacity-25" />
                                    <span>User does not have access to any managed columns.</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Restricted Columns - NOW STATIC / READ-ONLY */}
                {
                    loggedInUser.applicationAccess != 0 &&

                    <div className="col-md-6">
                        <div className="card shadow-sm border rounded-3 h-100 border-danger-subtle">
                            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold text-danger d-flex align-items-center gap-2">
                                    <FaLock /> Restricted Columns
                                </h6>
                                <Badge bg="danger" className="bg-opacity-10 rounded-pill">{restrictedColumns.length}</Badge>
                            </div>
                            <div className="card-body p-0 bg-theme-card overflow-auto" style={{ maxHeight: '350px' }}>
                                {restrictedColumns.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {restrictedColumns.map((col, idx) => (
                                            <li key={col.id} className={`list-group-item bg-transparent py-3 ${idx !== 0 ? 'border-top' : 'border-0'} d-flex align-items-center gap-3`}>
                                                <div className="bg-danger bg-opacity-10 text-danger p-2 rounded">
                                                    <FaLock size={16} />
                                                </div>
                                                <span className="fw-medium text-muted">{col.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="p-5 text-center text-muted d-flex flex-column align-items-center">
                                        <FaCheckCircle size={40} className="mb-3 text-success opacity-50" />
                                        <span>No columns are explicitly restricted.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                }
            </div>

            {/* Access Requests History Table */}
            {accessRequests.length > 0 && (
                <div className="card shadow-sm border rounded-3 overflow-hidden mt-2">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                            <FaClock className="text-secondary" /> Request History
                        </h6>
                    </div>
                    <div className="table-responsive mb-0 bg-theme-card">
                        <table className="table table-hover mb-0 align-middle">
                            <thead className="table-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4 py-3">Requested Column</th>
                                    <th className="py-3">Date</th>
                                    <th className="py-3 w-50">Justification</th>
                                    <th className="py-3 text-end pe-4">Status</th>
                                </tr>
                            </thead>
                            <tbody className="border-top-0">
                                {accessRequests.map(req => (
                                    <tr key={req.requestId}>
                                        <td className="ps-4 fw-bold text-dark">{req.columnName}</td>
                                        <td className="text-muted small">
                                            {new Date(req.requestDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </td>
                                        <td className="text-muted small text-truncate" style={{ maxWidth: '300px' }} title={req.reason}>
                                            <span className="fst-italic">"{req.reason}"</span>
                                        </td>
                                        <td className="text-end pe-4">{getStatusBadge(req.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Change Group Modal */}
            <Modal show={showGroupModal} onHide={() => !isUpdatingGroup && setShowGroupModal(false)} centered backdrop="static">
                <Modal.Header closeButton={!isUpdatingGroup} className="border-bottom">
                    <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
                        <FaExchangeAlt className="text-primary" /> Update Restriction Group
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light">
                    {isLoadingGroups ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="text-muted small mt-2">Loading available groups...</p>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            <Form.Group>
                                <Form.Label className="small fw-bold text-muted text-uppercase mb-1">Select Group</Form.Label>
                                <Form.Select
                                    value={selectedNewGroupId}
                                    onChange={(e) => setSelectedNewGroupId(e.target.value)}
                                    className="py-2 shadow-sm border-0"
                                    disabled={isUpdatingGroup}
                                >
                                    <option value="none">No Restriction Group (Full Access)</option>
                                    {allGroups.map(grp => (
                                        <option key={grp.columnRestrictionId} value={grp.columnRestrictionId.toString()}>
                                            {grp.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {/* Live Preview Box */}
                            <div className="card border-0 shadow-sm mt-2">
                                <div className="card-body p-3">
                                    <h6 className="fw-bold fs-6 border-bottom pb-2 mb-3">Group Access Preview</h6>

                                    <div className="mb-3">
                                        <span className="small fw-bold text-success d-flex align-items-center gap-1 mb-2">
                                            <FaUnlock /> Columns Granted (Accessible)
                                        </span>
                                        <div className="d-flex flex-wrap gap-2">
                                            {previewAllowedCols.length > 0 ? previewAllowedCols.map(col => (
                                                <Badge key={col} bg="success" className="bg-opacity-10 fw-normal border border-success border-opacity-25 py-1 px-2">
                                                    {col}
                                                </Badge>
                                            )) : (
                                                <span className="text-muted small fst-italic">No managed columns granted.</span>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <span className="small fw-bold text-danger d-flex align-items-center gap-1 mb-2">
                                            <FaLock /> Columns Blocked (Restricted)
                                        </span>
                                        <div className="d-flex flex-wrap gap-2">
                                            {previewRestrictedCols.length > 0 ? previewRestrictedCols.map(col => (
                                                <Badge key={col} bg="danger" className="bg-opacity-10 fw-normal border border-danger border-opacity-25 py-1 px-2">
                                                    {col}
                                                </Badge>
                                            )) : (
                                                <span className="text-muted small fst-italic">No columns blocked.</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-top bg-white">
                    <Button variant="light" onClick={() => setShowGroupModal(false)} disabled={isUpdatingGroup}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveNewGroup}
                        disabled={isLoadingGroups || isUpdatingGroup || (assignedGroup?.columnRestrictionId.toString() === selectedNewGroupId && selectedNewGroupId !== "none")}
                        className="d-flex align-items-center gap-2 px-4 shadow-sm"
                    >
                        {isUpdatingGroup ? <Spinner size="sm" animation="border" /> : <FaCheckCircle />}
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </motion.div>
    );
};

export default UserColumnSecurity;